import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "utils/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';
import GetAppIcon from "@mui/icons-material/GetApp";
import moment from "dayjs";
import _ from "lodash";
import CustomInputDatePicker from "components/Common/CustomInputDatePicker";
import { ButtonIcon, ApplyButton } from "components/Common/Buttons";
import { TableGenerator } from "components/Common";
import { useSnackbar } from "notistack";
import AsyncAutoComplete from "../common/AsyncAutoComplete";
import { downloadBlob, handleServerErrors } from "helpers";
import {
  getFilterEndDate,
  getFilterStartDate,
  getTimeZoneDifference,
} from "utils/helpers";
import { ModeList, fields } from "./constants";
import styles from "./styles";

const TransactonsDetails = () => {
  const user = useSelector((state) => state.userReducer?.current_user);
  const areasData = useSelector((state) => state.dropdownReducer.areasData);
  const locationsData = useSelector(
    (state) => state.dropdownReducer.locationsData
  );
  const vmData = useSelector((state) => state.dropdownReducer.devicesData);
  const transactionStatusData = useSelector(
    (state) => state.dropdownReducer.transactionStatusData
  );
  const classes = styles();
  const { enqueueSnackbar } = useSnackbar();

  const selectedOperator =
    user.type !== "SA" && user?.company?.company_id
      ? { value: user.company.company_id, label: user.company.company_name }
      : { value: "all", label: "All" };

  const [startDate, setStartDate] = useState(
    moment().subtract(1, "days").toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [locationList, setLocationList] = useState(locationsData || []);
  const [data, setData] = useState([]);
  const [vmList, setVmList] = useState(vmData || []);
  const [locationListLoading, setLocationListLoading] = useState(false);
  const [vmListLoading, setVmListLoading] = useState(false);
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [loader, setLoader] = useState(true);
  const [filters, setFilters] = useState({
    area: { value: "all", label: "All" },
    location: { value: "all", label: "All" },
    vm: [{ value: "all", label: "All" }],
    status: { value: "all", label: "All" },
    mode: { value: "all", label: "All" },
  });

  const [loading, setLoading] = useState(false);

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const fetchTableData = async (rowsPerPage, order, page = 0) => {
    try {
      setLoader(true);
      const params = {
        start_date: getFilterStartDate(startDate),
        end_date: getFilterEndDate(endDate),
        _scope: "AND",
        limit: rowsPerPage,
        page: page + 1,
      };
      if (order) params.ordering = order;
      if (filters.area?.value !== "all")
        params.device__location__area_id = filters.area?.value;
      if (filters.location?.value !== "all")
        params.device__location_id = filters.location?.value;
      if (filters.status?.value !== "all")
        params.status = filters.status?.value;
      if (filters.mode?.value !== "all") {
        params.mode = filters.mode?.value;
      }
      if (filters.vm[0]?.value !== "all" && filters.vm?.length >= 1)
        params.device_id = filters.vm?.map((item) => item?.value).join(",");
      const { data } = await window.axiosIns(
        "transactions/custom-transactions",
        { params }
      );
      setData(data.results);
      setNextPage(data.next);
      setPreviousPage(data.previous);
    } catch (err) {
    } finally {
      setLoader(false);
    }
  };

  const changePage = async (url) => {
    setLoader(true);
    window.axiosIns
      .get(url)
      .then(({ data = {} }) => {
        setData(data.results);
        setNextPage(data.next);
        setPreviousPage(data.previous);
      })
      .catch((err) => {
        setLoader(false);
        if (err.detail) {
          enqueueSnackbar(err.detail);
        } else {
          handleServerErrors(
            err,
            enqueueSnackbar,
            "Could not get data. Try again."
          );
        }
      })
      .finally(() => setLoader(false));
  };

  const fetchLocations = async (areaList = [], area_id, selectedOperator) => {
    try {
      setLocationListLoading(true);
      if (area_id && area_id !== "all") {
        const list =
          _.find(areaList, (x) => x.value === area_id)?.locations || [];

        setLocationList([
          { label: "All", value: "all" },
          ..._.map(list, ({ location_id, location_name }) => ({
            label: location_name,
            value: location_id,
          })),
        ]);
      } else {
        if (selectedOperator && selectedOperator?.value !== "all") {
          let newLocation = [];
          const locationData = [
            ..._.map(areaList, ({ locations }) =>
              Array.prototype.push.apply(newLocation, locations)
            ),
          ];
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(newLocation, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        } else {
          const { data } = await window.axiosIns("locations/locations", {
            params: {
              all: true,
              state: "all",
              ordering: "location_name",
              lite: true,
            },
          });
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(data?.results, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLocationListLoading(false);
    }
  };

  const fetchVM = async (selectedOperator, selectedArea, selectedLocation) => {
    try {
      setVmListLoading(true);
      setVmList([]);
      const params = {
        all: true,
        ordering: "vm_name",
        lite: true,
      };
      if (selectedOperator && selectedOperator.value !== "all")
        params.company_id = selectedOperator.value;
      if (selectedArea && selectedArea.value !== "all")
        params.area_id = selectedArea.value;
      if (selectedLocation && selectedLocation.value !== "all")
        params.location = selectedLocation.value;
      const { data } = await window.axiosIns("device", {
        params,
      });
      setVmList([
        { label: "All", value: "all" },
        ..._.map(data?.data?.results, ({ id, serial_number, vm_name }) => ({
          label: `${vm_name ? `${vm_name} ||` : ""} ${serial_number}`,
          value: id,
        })),
      ]);
    } catch (err) {
      console.log("err", err);
      setVmListLoading(false);
    } finally {
      setVmListLoading(false);
    }
  };

  const isDateRangeTooLarge = useMemo(() => {
    if (!startDate || !endDate) return false;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffInMs = end.getTime() - start.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays > 90;
  }, [startDate, endDate]);

  const fetchReport = async (type) => {
    setLoading(true);
    const params = {
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      tz: getTimeZoneDifference(),
      _scope: "AND",
    };

    if (type === "pdf") {
      params.type = "pdf";
      params.model = "transactions_summary";
    }

    if (filters.vm[0]?.value && filters.vm[0]?.value !== "all") {
      params["device_id"] = filters.vm?.map((item) => item?.value).join(",");
    }
    if (filters.location.value && filters.location.value !== "all") {
      params["device__location_id"] = filters.location.value;
    }
    if (filters.area.value && filters.area.value !== "all") {
      params["device__location__area_id"] = filters.area.value;
    }
    if (selectedOperator.value && selectedOperator.value !== "all") {
      params["device__company_id"] = selectedOperator.value;
    }
    if (filters.status.value && filters.status?.value !== "all") {
      params.status = filters.status?.value;
    }
    if (filters.mode.value && filters.mode?.value !== "all") {
      params["mode"] = filters.mode?.value;
    }

    try {
      const url =
        type === "pdf"
          ? "transactions/export"
          : "transactions/export_transaction_summary";

      const { data } = await window.axiosIns(url, {
        responseType: "blob",
        params,
      });

      if (data) {
        const fileName =
          type === "pdf"
            ? "transactions_summary_reports.pdf"
            : "transactions_report.xlsx";
        const fileType =
          type === "pdf" ? undefined : { type: "application/xlsx" };

        downloadBlob(new Blob([data], fileType), fileName);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPDF = () => fetchReport("pdf");

  const fetchExcel = () => fetchReport("excel");

  useEffect(() => {
    fetchTableData(rowsPerPage, null, page);
  }, []);

  const handleArea = (val) => {
    updateFilter("area", val);
    fetchLocations(areasData, val.value, selectedOperator);
    fetchVM(selectedOperator, val);
    updateFilter("location", {
      value: "all",
      label: "All",
    });
    updateFilter("vm", [{ value: "all", label: "All" }]);
  };

  const handleLocation = (val) => {
    updateFilter("location", val);
    fetchVM(selectedOperator, filters.area, val);
    updateFilter("vm", [{ value: "all", label: "All" }]);
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <Grid spacing={1} container style={{ marginBottom: "1rem" }} className={classes.filterBar}>
        <Grid xs={10}>
          <Grid container spacing={1}>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={startDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => setStartDate(date)}
                label="Start Date"
                maxDate={new Date(endDate).setDate(new Date(endDate).getDate())}
                disabled={locationListLoading || vmListLoading}
              />
            </Grid>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={endDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => setEndDate(date)}
                label="End Date"
                minDate={new Date(startDate)}
                maxDate={new Date()}
                disabled={locationListLoading || vmListLoading}
              />
            </Grid>

            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => updateFilter("mode", val)}
                options={ModeList}
                value={filters.mode}
                label="Transaction Mode"
                disabled={locationListLoading || vmListLoading}
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => updateFilter("status", val)}
                options={transactionStatusData || []}
                value={filters.status}
                loading={false}
                label="Transaction Status"
                disabled={locationListLoading || vmListLoading}
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleArea}
                loading={false}
                value={filters.area}
                options={areasData || []}
                label="Area"
                disabled={locationListLoading || vmListLoading}
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleLocation}
                value={filters.location}
                loading={locationListLoading}
                options={locationList}
                label="Location"
                disabled={locationListLoading || vmListLoading}
              />
            </Grid>
            <Grid xs={3}>
              <div className="d-flex" style={{ gap: "8px" }}>
                <Autocomplete
                  multiple
                  disabled={locationListLoading || vmListLoading}
                  style={{ width: "100%" }}
                  id="tags-outlined"
                  value={filters.vm}
                  loading={vmListLoading}
                  options={vmList || []}
                  defaultValue={[{ label: "All", value: "all" }]}
                  onChange={(event, newValue) => {
                    const allPos = _.findIndex(
                      newValue,
                      (x) => x.value === "all"
                    );
                    if (allPos === 0 && newValue?.length > 1) {
                      const data = _.filter(newValue, (x) => x.value !== "all");
                      updateFilter("vm", data);
                    } else if (allPos > 0) {
                      const data = _.filter(newValue, (x) => x.value === "all");
                      updateFilter("vm", data);
                    } else {
                      updateFilter("vm", newValue);
                    }
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  renderTags={(tagValue, getTagProps) => {
                    if (tagValue.length < 2) {
                      return tagValue.map((option, index) => {
              const { key, ...tp } = getTagProps({ index });
              return (
                <Chip key={key}
                          {...tp}
                          label={`${option.label.slice(0, 8)}...`}
                        />
              );
            });
                    } else {
                      return (
                        <>
                          <div>
                            {(more ? tagValue : tagValue.slice(0, 1)).map((option, index) => {
              const { key, ...tp } = getTagProps({ index });
              return (
                <Chip key={key}
                                  {...tp}
                                  label={`${option.label.slice(0, 8)}...`}
                                />
              );
            })}
                            {!more && (
                              <span
                                style={{
                                  position: "absolute",
                                  fontSize: "13px",
                                  top: "20px",
                                  cursor: "pointer",
                                }}
                                onClick={() => setMore(!more)}
                              >{`+${tagValue.length - 1} more`}</span>
                            )}
                          </div>
                          {more && (
                            <span
                              style={{
                                position: "static",
                                paddingLeft: "10px",
                                fontSize: "13px",
                                top: "20px",
                                cursor: "pointer",
                              }}
                              onClick={() => setMore(!more)}
                            >
                              show less
                            </span>
                          )}
                        </>
                      );
                    }
                  }}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Vending Machines"
                      slotProps={{ ...params.slotProps, input: { ...params.slotProps.input,
                        endAdornment: (
                          <React.Fragment>
                            {vmListLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.slotProps.input.endAdornment}
                          </React.Fragment>
                        ),
                      } }}
                    />
                  )}
                />
              </div>
            </Grid>
            <Grid xs={3} />
          </Grid>
        </Grid>
        <Grid xs={2}>
          <ApplyButton
            size="large"
            fullWidth
            onClick={() => fetchTableData(rowsPerPage, null, 0)}
            style={{ marginBottom: "4px", height: 40, padding: "0" }}
            disabled={locationListLoading || vmListLoading}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <ButtonIcon
              label="Excel"
              size="large"
              fullWidth
              Icon={GetAppIcon}
              onClick={fetchExcel}
              disabled={loading || data?.length <= 0 || isDateRangeTooLarge}
              style={{ marginRight: "4px", height: 40, padding: "0" }}
            />
            <ButtonIcon
              label="PDF"
              size="large"
              fullWidth
              Icon={GetAppIcon}
              onClick={fetchPDF}
              disabled={loading || data?.length <= 0}
              style={{ height: 40, padding: "0" }}
            />
          </div>
        </Grid>
      </Grid>
      <TableGenerator
        loader={loader}
        data={data}
        minimal={true}
        nextPage={nextPage}
        previousPage={previousPage}
        fields={fields}
        showSelectAll={false}
        sensorTable={true}
        backendPagination={true}
        disablePagination={false}
        onRowPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setPage(0);
          fetchTableData(rows, null, 0);
        }}
        rowOnePage={rowsPerPage}
        currentPage={page}
        handleSortChange={(ordering) => {
          setPage(0);
          fetchTableData(rowsPerPage, ordering, 0);
        }}
        onPageChange={(page, direction) => {
          setPage(page);
          if (direction === "next") {
            changePage(nextPage);
          } else if (direction === "back") {
            changePage(previousPage);
          }
        }}
      />
    </div>
  );
};

export default TransactonsDetails;
